import React, { useRef, useEffect } from 'react';

interface TeamMember {
    playerId: number;
    playerName: string;
    average: number;
    handicap: number;
}

interface Match {
    id: number;
    squadName: string;
    squadDate: string;
    scheduledTime: string;
    team1Name: string;
    team2Name: string;
    team1Lane: number;
    team2Lane: number;
}

interface ScoresPrintViewProps {
    match: Match;
    team1Members: TeamMember[];
    team2Members: TeamMember[];
    gamesCount: number;
    scores: Record<string, number | undefined>;
    bakerScores?: Record<string, number | undefined>;
    bakerGames?: number[];
    leagueName?: string;
    matchResults?: {
        team1SeriesTotal: number;
        team2SeriesTotal: number;
        team1SeriesHandicap: number;
        team2SeriesHandicap: number;
        team1Points: number;
        team2Points: number;
        gameWinners: number[];
        seriesWinner: number;
        seriesWinnerHandicap: number;
    };
    onClose?: () => void;
}

const ScoresPrintView: React.FC<ScoresPrintViewProps> = ({
    match,
    team1Members,
    team2Members,
    gamesCount,
    scores,
    bakerScores = {},
    bakerGames = [],
    leagueName,
    matchResults,
    onClose
}) => {
    const printContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Match Scores - ${match.squadName}</title>
      <style>
        body { font-family: Arial, sans-serif; margin: 0; padding: 20px; background: white; color: black; }
        .header { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 30px; border-bottom: 2px solid #000; padding-bottom: 10px; }
        .header-center { flex: 1; text-align: center; }
        .header-logo { width: 80px; text-align: right; }
        .header-spacer { width: 80px; }
        .title { font-size: 24px; font-weight: bold; margin-bottom: 10px; }
        .subtitle { font-size: 16px; margin-bottom: 20px; }
        .teams { display: block; margin-bottom: 30px; }
        .team { width: 100%; max-width: 600px; margin: 0 auto 24px auto; border: 1px solid #000; border-radius: 8px; padding: 16px; background: #f8f8ff; }
        .team-title { font-size: 18px; font-weight: bold; margin-bottom: 10px; text-align: center; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
        th, td { border: 1px solid #000; padding: 8px; text-align: center; }
        th { background: #e0e7ff; }
        .totals { font-weight: bold; background: #e0e7ff; }
        .winner { background: #c6f6d5; }
        .loser { background: #fed7d7; }
        .footer { margin-top: 30px; text-align: center; font-size: 12px; border-top: 1px solid #000; padding-top: 10px; }
        @media print {
          .header, .footer { page-break-inside: avoid; }
          .team { page-break-inside: avoid; }
          .teams { display: block !important; }
        }
      </style>
    </head>
    <body>
      <div class="header">
        <div class="header-spacer"></div>
        <div class="header-center">
          <div class="title">Match Scores${leagueName ? ` — ${leagueName}` : ''}</div>
          <div class="subtitle">${match.squadName} • ${new Date(match.squadDate).toLocaleDateString()} • ${match.scheduledTime}</div>
          <div class="subtitle">${match.team1Name} vs ${match.team2Name}</div>
          <div class="subtitle">Lane ${match.team1Lane} &amp; ${match.team2Lane}</div>
        </div>
        <div class="header-logo">
          <img src="${window.location.origin}/BowlerProLogo1.png" alt="BowlerPro" style="height:44px; width:auto;" onerror="this.style.display='none'" />
        </div>
      </div>
      <div class="teams">
        ${[team1Members, team2Members].map((members, idx) => {
        const teamName = idx === 0 ? match.team1Name : match.team2Name;
        const teamLane = idx === 0 ? match.team1Lane : match.team2Lane;
        return `
            <div class="team">
              <div class="team-title">${teamName} (Lane ${teamLane})</div>
              <table>
                <tr>
                  <th>Player</th>
                  <th>Avg</th>
                  <th>Hdcp</th>
                  ${Array.from({ length: gamesCount }, (_, i) => `<th>G${i + 1}</th>`).join('')}
                  <th>Series</th>
                  <th>Series w/Hdcp</th>
                </tr>
                ${members.map(member => {
            const series = Array.from({ length: gamesCount }, (_, i) => {
                const score = scores[`${member.playerId}-${i + 1}`];
                return score ?? '';
            });
            const seriesTotal = series.reduce<number>((sum, val) => sum + (typeof val === 'number' ? val : 0), 0);
            const seriesHdcpTotal = seriesTotal + member.handicap * gamesCount;
            return `
                    <tr>
                      <td>${member.playerName}</td>
                      <td>${member.average}</td>
                      <td>${member.handicap}</td>
                      ${series.map(s => `<td>${s}</td>`).join('')}
                      <td>${seriesTotal}</td>
                      <td>${seriesHdcpTotal}</td>
                    </tr>
                  `;
        }).join('')}
              </table>
            </div>
          `;
    }).join('')}
      </div>
      <div>
        <table>
          <tr>
            <th></th>
            ${Array.from({ length: gamesCount }, (_, i) => `<th>Game ${i + 1}</th>`).join('')}
            <th>Series</th>
            <th>Series w/Hdcp</th>
            <th>Points</th>
          </tr>
          <tr class="totals">
            <td>${match.team1Name}</td>
            ${Array.from({ length: gamesCount }, (_, i) => {
        const gameNumber = i + 1;
        // Check if this is a baker game
        if (bakerGames.includes(gameNumber)) {
            const bakerScore = bakerScores[`team1-${gameNumber}`];
            return `<td>${bakerScore ?? 0}</td>`;
        }
        // Regular game - sum individual scores
        const total = team1Members.reduce((sum, member) => {
            const score = scores[`${member.playerId}-${gameNumber}`];
            return sum + (typeof score === 'number' ? score : 0);
        }, 0);
        return `<td>${total}</td>`;
    }).join('')}
            <td>${matchResults?.team1SeriesTotal ?? ''}</td>
            <td>${matchResults?.team1SeriesHandicap ?? ''}</td>
            <td>${matchResults?.team1Points ?? ''}</td>
          </tr>
          <tr class="totals">
            <td>${match.team2Name}</td>
            ${Array.from({ length: gamesCount }, (_, i) => {
        const gameNumber = i + 1;
        // Check if this is a baker game
        if (bakerGames.includes(gameNumber)) {
            const bakerScore = bakerScores[`team2-${gameNumber}`];
            return `<td>${bakerScore ?? 0}</td>`;
        }
        // Regular game - sum individual scores
        const total = team2Members.reduce((sum, member) => {
            const score = scores[`${member.playerId}-${gameNumber}`];
            return sum + (typeof score === 'number' ? score : 0);
        }, 0);
        return `<td>${total}</td>`;
    }).join('')}
            <td>${matchResults?.team2SeriesTotal ?? ''}</td>
            <td>${matchResults?.team2SeriesHandicap ?? ''}</td>
            <td>${matchResults?.team2Points ?? ''}</td>
          </tr>
        </table>
      </div>
      <div class="footer">
        Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}
      </div>
    </body>
    </html>
  `;

    const hasPrinted = useRef(false);

    useEffect(() => {
        if (hasPrinted.current) return;
        hasPrinted.current = true;

        const printWindow = window.open('', '_blank');
        if (printWindow) {
            printWindow.document.write(printContent);
            printWindow.document.close();
            printWindow.focus();

            printWindow.onbeforeunload = () => {
                if (typeof onClose === 'function') {
                    onClose();
                }
            };


            printWindow.print();

        }
    }, []);

    return null;
};


export default ScoresPrintView;